import { Modal } from '@/components/admin/Modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SingleSelectFilter } from '@/components/ui/single-select-filter';
import { Textarea } from '@/components/ui/textarea';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type {
  ResponsePromptTestCase,
  ResponsePromptVariable,
  ResponsePromptVariant,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  fileId: string;
  currentContent: string;
  currentVariables: ResponsePromptVariable[];
  /** prompt 의 전체 variant — variant 선택 + 매트릭스 실행에 사용. */
  variants: ResponsePromptVariant[];
  /**
   * 매트릭스 실행 시 각 variant 의 본문/변수는 그 variant 의 currentContent/currentVariables 를
   * 사용. 단일 실행 (또는 "현재 편집 본문") 은 currentContent prop 사용.
   */
}

interface RunResult {
  pass: boolean | null; // null = expectedContains 없음 = 단순 실행
  output: string;
  durationMs: number;
  error?: string;
}

function variantLabel(v: ResponsePromptVariant): string {
  if (v.label?.trim()) return v.label.trim();
  if (v.modelMatch === '') return '기본';
  return v.modelMatch;
}

/**
 * 회귀 테스트 케이스 — 미리 정의된 variableValues + expectedContains 로 본문을 실행.
 *  - 단일 실행: 현재 편집 본문 + 선택된 variant (audit 용)
 *  - 매트릭스: 각 variant 의 활성 본문 × 각 케이스 → grid (variant 간 동질성 검증)
 * 실행 결과는 모달 내 임시 state — 페이지 새로고침 시 사라짐.
 */
export function TestCasesModal({
  open,
  onClose,
  fileId,
  currentContent,
  currentVariables,
  variants,
}: Props) {
  const qc = useQueryClient();
  const listQ = useQuery({
    queryKey: ['admin', 'response-prompts', 'test-cases', fileId],
    queryFn: () => api.responsePromptTestCasesList(fileId),
    enabled: open,
  });

  // 추가 폼
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [expectedContains, setExpectedContains] = useState('');
  const [note, setNote] = useState('');

  /** 단일 실행 시 사용할 variant id — '' 면 첫 variant (대개 default). */
  const [runVariantId, setRunVariantId] = useState<string>('');

  // 모달 열 때 첫 variant 자동 선택.
  useEffect(() => {
    if (open && variants.length > 0 && !variants.find((v) => v.id === runVariantId)) {
      const def = variants.find((v) => v.modelMatch === '') ?? variants[0];
      if (def) setRunVariantId(def.id);
    }
  }, [open, variants, runVariantId]);

  /** 단일 실행 결과 (caseId → RunResult). */
  const [singleResults, setSingleResults] = useState<Record<string, RunResult>>({});
  /** 매트릭스 결과 (caseId → variantId → RunResult). */
  const [matrixResults, setMatrixResults] = useState<Record<string, Record<string, RunResult>>>({});
  const [matrixRunning, setMatrixRunning] = useState(false);
  /** 매트릭스 셀 클릭 시 detail 표시. `${caseId}::${variantId}`. */
  const [matrixDetailKey, setMatrixDetailKey] = useState<string | null>(null);
  /** 매트릭스 실행 중 abort — 모달 닫기 / 취소 버튼이 fire. */
  const matrixAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!open) {
      // in-flight LLM 호출 abort — 비용 절약 + 사라진 컴포넌트의 setState 회피.
      matrixAbortRef.current?.abort();
      setShowAdd(false);
      setSingleResults({});
      setMatrixResults({});
      setMatrixDetailKey(null);
    }
  }, [open]);

  useEffect(() => {
    if (showAdd) {
      setName('');
      const seed: Record<string, string> = {};
      for (const v of currentVariables) seed[v.key] = v.defaultValue;
      setValues(seed);
      setExpectedContains('');
      setNote('');
    }
  }, [showAdd, currentVariables]);

  const createMut = useMutation({
    mutationFn: () =>
      api.responsePromptTestCaseCreate(fileId, {
        name,
        variableValues: values,
        expectedContains: expectedContains.trim() || undefined,
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      setShowAdd(false);
      qc.invalidateQueries({ queryKey: ['admin', 'response-prompts', 'test-cases', fileId] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (caseId: string) => api.responsePromptTestCaseDelete(fileId, caseId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'response-prompts', 'test-cases', fileId] });
    },
  });

  /** 단일 실행 — 현재 편집 본문 + selected variant. */
  const runCase = async (c: ResponsePromptTestCase) => {
    setSingleResults((prev) => ({
      ...prev,
      [c.id]: { pass: null, output: '실행 중…', durationMs: 0 },
    }));
    try {
      const r = await api.responsePromptRunTest({
        content: currentContent,
        variableValues: c.variableValues,
        ...(runVariantId ? { variantId: runVariantId } : {}),
      });
      const pass = c.expectedContains ? r.output.includes(c.expectedContains) : null;
      setSingleResults((prev) => ({
        ...prev,
        [c.id]: { pass, output: r.output, durationMs: r.durationMs },
      }));
    } catch (e) {
      setSingleResults((prev) => ({
        ...prev,
        [c.id]: {
          pass: false,
          output: '',
          durationMs: 0,
          error: e instanceof ApiError ? e.message : e instanceof Error ? e.message : '실패',
        },
      }));
    }
  };

  const runAll = async () => {
    const cases = listQ.data?.cases ?? [];
    await cases.reduce<Promise<unknown>>((acc, c) => acc.then(() => runCase(c)), Promise.resolve());
  };

  /**
   * 매트릭스 — 모든 variant × 모든 케이스. 각 셀은 그 variant 의 활성 본문으로 실행.
   * 직렬 실행 (LLM concurrency 부담 최소화). 진행 중인 셀은 임시로 "…" 표시.
   */
  const runMatrix = async () => {
    const cases = listQ.data?.cases ?? [];
    if (cases.length === 0 || variants.length === 0) return;
    const ctl = new AbortController();
    matrixAbortRef.current = ctl;
    setMatrixRunning(true);
    setMatrixResults({});
    setMatrixDetailKey(null);
    try {
      const tasks: Array<{ c: ResponsePromptTestCase; v: ResponsePromptVariant }> = [];
      for (const c of cases) for (const v of variants) tasks.push({ c, v });
      await tasks.reduce<Promise<unknown>>(
        (acc, { c, v }) =>
          acc.then(async () => {
            if (ctl.signal.aborted) return; // 취소되면 남은 셀 skip
            if (v.currentContent == null) {
              setMatrixResults((prev) => ({
                ...prev,
                [c.id]: {
                  ...(prev[c.id] ?? {}),
                  [v.id]: {
                    pass: false,
                    output: '',
                    durationMs: 0,
                    error: '활성 본문 없음',
                  },
                },
              }));
              return;
            }
            try {
              const r = await api.responsePromptRunTest(
                {
                  content: v.currentContent,
                  variableValues: c.variableValues,
                  variantId: v.id,
                },
                ctl.signal,
              );
              const pass = c.expectedContains ? r.output.includes(c.expectedContains) : null;
              setMatrixResults((prev) => ({
                ...prev,
                [c.id]: {
                  ...(prev[c.id] ?? {}),
                  [v.id]: { pass, output: r.output, durationMs: r.durationMs },
                },
              }));
            } catch (e) {
              if (ctl.signal.aborted) return; // 취소된 호출은 조용히
              setMatrixResults((prev) => ({
                ...prev,
                [c.id]: {
                  ...(prev[c.id] ?? {}),
                  [v.id]: {
                    pass: false,
                    output: '',
                    durationMs: 0,
                    error:
                      e instanceof ApiError ? e.message : e instanceof Error ? e.message : '실패',
                  },
                },
              }));
            }
          }),
        Promise.resolve(),
      );
    } finally {
      matrixAbortRef.current = null;
      setMatrixRunning(false);
    }
  };

  const cancelMatrix = () => {
    matrixAbortRef.current?.abort();
  };

  const cases = listQ.data?.cases ?? [];
  const variableKeys = currentVariables.map((v) => v.key);

  /** variant select 옵션 (단일 실행용). default 좌측 + 그 외 정렬. */
  const variantSelectOptions = useMemo(() => {
    const sorted = [...variants].sort((a, b) => {
      if (a.modelMatch === '' && b.modelMatch !== '') return -1;
      if (a.modelMatch !== '' && b.modelMatch === '') return 1;
      return a.modelMatch.localeCompare(b.modelMatch);
    });
    return sorted.map((v) => ({
      id: v.id,
      label: variantLabel(v),
      hint: v.modelMatch === '' ? 'default' : v.modelMatch,
    }));
  }, [variants]);

  const hasMatrixResults = Object.keys(matrixResults).length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`테스트 케이스${cases.length > 0 ? ` (${cases.length})` : ''}`}
      description="회귀 검증용 — variant 선택 후 단일/전체 실행, 또는 매트릭스로 모든 variant × 모든 케이스 비교."
      size="xl"
      footer={
        <div className="flex justify-between gap-2">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={runAll}
              disabled={cases.length === 0 || matrixRunning}
            >
              <Play className="size-4 mr-1" />
              전체 실행 (선택 variant)
            </Button>
            {matrixRunning ? (
              <Button type="button" variant="secondary" onClick={cancelMatrix}>
                매트릭스 취소
              </Button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                onClick={runMatrix}
                disabled={cases.length === 0 || variants.length === 0}
              >
                매트릭스 실행 (모든 variant)
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={() => setShowAdd((v) => !v)}>
              {showAdd ? '취소' : '+ 케이스 추가'}
            </Button>
            <Button type="button" onClick={onClose}>
              닫기
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        {/* 단일 실행용 variant 선택 */}
        {variants.length > 0 ? (
          <div className="flex items-center gap-2 rounded-md border border-border bg-surface-muted/30 px-3 py-2">
            <span className="text-xs text-foreground-muted shrink-0">단일/전체 실행 variant:</span>
            <div className="flex-1 max-w-xs">
              <SingleSelectFilter
                options={variantSelectOptions}
                value={runVariantId || null}
                onChange={(id) => setRunVariantId(id ?? '')}
                triggerPlaceholder="선택"
                searchable={variants.length > 6}
                clearable={false}
                triggerClassName="h-7 text-xs"
              />
            </div>
            <span className="text-[10px] text-foreground-muted">
              본문은 에디터에서 편집 중인 내용 사용
            </span>
          </div>
        ) : null}

        {/* 매트릭스 결과 그리드 */}
        {hasMatrixResults || matrixRunning ? (
          <div className="rounded-md border border-border">
            <div className="border-b border-border bg-surface-muted/30 px-3 py-1.5 text-xs font-medium">
              매트릭스 결과 — 행: 케이스, 열: variant. 셀 클릭 시 본문 표시.
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-surface-muted/20">
                    <th className="px-2 py-1.5 text-left font-medium">케이스</th>
                    {variants.map((v) => (
                      <th key={v.id} className="px-2 py-1.5 text-left font-medium">
                        {variantLabel(v)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cases.map((c) => (
                    <tr key={c.id} className="border-b border-border last:border-0">
                      <td className="px-2 py-1.5 truncate max-w-45">{c.name}</td>
                      {variants.map((v) => {
                        const r = matrixResults[c.id]?.[v.id];
                        const key = `${c.id}::${v.id}`;
                        const isSelected = matrixDetailKey === key;
                        return (
                          <td key={v.id} className="px-1 py-1">
                            <button
                              type="button"
                              onClick={() => setMatrixDetailKey(isSelected ? null : key)}
                              disabled={!r}
                              className={cn(
                                'inline-flex min-w-12 items-center justify-center rounded px-2 py-0.5 text-[10px]',
                                !r
                                  ? 'bg-surface-muted text-foreground-muted cursor-not-allowed'
                                  : r.error
                                    ? 'bg-destructive/15 text-destructive cursor-pointer hover:bg-destructive/25'
                                    : r.pass === true
                                      ? 'bg-emerald-500/15 text-emerald-600 cursor-pointer hover:bg-emerald-500/25'
                                      : r.pass === false
                                        ? 'bg-destructive/15 text-destructive cursor-pointer hover:bg-destructive/25'
                                        : 'bg-accent/15 text-accent cursor-pointer hover:bg-accent/25',
                                isSelected && 'ring-2 ring-accent',
                              )}
                              title={r?.error ?? `${r?.durationMs ?? '—'}ms`}
                            >
                              {!r
                                ? '…'
                                : r.error
                                  ? 'err'
                                  : r.pass === true
                                    ? '✓'
                                    : r.pass === false
                                      ? '✗'
                                      : '·'}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {matrixDetailKey
              ? (() => {
                  const [cId, vId] = matrixDetailKey.split('::');
                  const c = cases.find((x) => x.id === cId);
                  const v = variants.find((x) => x.id === vId);
                  const r = cId && vId ? matrixResults[cId]?.[vId] : undefined;
                  if (!c || !v || !r) return null;
                  return (
                    <div className="border-t border-border bg-surface-muted/10 p-3 space-y-1">
                      <div className="text-xs text-foreground-muted">
                        <strong>{c.name}</strong> × <strong>{variantLabel(v)}</strong> ·{' '}
                        {r.durationMs}ms
                        {c.expectedContains ? (
                          <span className="ml-2">
                            expected: <code className="font-mono">{c.expectedContains}</code>
                          </span>
                        ) : null}
                      </div>
                      {r.error ? (
                        <div className="text-xs text-destructive">{r.error}</div>
                      ) : (
                        <pre className="whitespace-pre-wrap wrap-break-word font-mono text-xs">
                          {r.output}
                        </pre>
                      )}
                    </div>
                  );
                })()
              : null}
          </div>
        ) : null}

        {showAdd ? (
          <div className="rounded-md border border-accent/40 bg-accent/5 p-3 space-y-2">
            <div>
              <label className="block text-xs text-foreground-muted mb-1" htmlFor="tc-name">
                케이스 이름
              </label>
              <Input
                id="tc-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="예: 한국어 질문 처리"
              />
            </div>
            {variableKeys.length > 0 ? (
              <div>
                <div className="text-xs text-foreground-muted mb-1">변수 값</div>
                <div className="space-y-1">
                  {variableKeys.map((k) => (
                    <div key={k} className="flex items-center gap-2">
                      <code className="text-[10px] text-foreground-muted w-32 shrink-0 font-mono">
                        {`{{${k}}}`}
                      </code>
                      <Input
                        value={values[k] ?? ''}
                        onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                        className="h-7 text-xs"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div>
              <label className="block text-xs text-foreground-muted mb-1" htmlFor="tc-expected">
                expectedContains (선택) — 결과에 이 문자열이 포함돼야 통과
              </label>
              <Input
                id="tc-expected"
                value={expectedContains}
                onChange={(e) => setExpectedContains(e.target.value)}
                placeholder="예: 한국어"
              />
            </div>
            <div>
              <label className="block text-xs text-foreground-muted mb-1" htmlFor="tc-note">
                메모 (선택)
              </label>
              <Textarea
                id="tc-note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowAdd(false)}
                disabled={createMut.isPending}
              >
                취소
              </Button>
              <Button
                type="button"
                onClick={() => createMut.mutate()}
                disabled={createMut.isPending || !name.trim()}
              >
                {createMut.isPending ? '저장 중…' : '저장'}
              </Button>
            </div>
            {createMut.error ? (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {createMut.error instanceof ApiError ? createMut.error.message : '저장 실패'}
              </div>
            ) : null}
          </div>
        ) : null}

        {listQ.isLoading ? (
          <div className="py-4 text-center text-sm text-foreground-muted">불러오는 중…</div>
        ) : cases.length === 0 ? (
          <div className="py-6 text-center text-sm text-foreground-muted">
            아직 저장된 케이스가 없습니다.
          </div>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {cases.map((c) => {
              const result = singleResults[c.id];
              return (
                <li key={c.id} className="px-3 py-3 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">{c.name}</span>
                    {result?.pass === true ? (
                      <Badge
                        variant="muted"
                        className="bg-emerald-500/15 text-emerald-600 border-emerald-500/40"
                      >
                        ✓ 통과
                      </Badge>
                    ) : null}
                    {result?.pass === false ? (
                      <Badge
                        variant="muted"
                        className="bg-destructive/15 text-destructive border-destructive/40"
                      >
                        ✗ 실패
                      </Badge>
                    ) : null}
                    {result && result.pass === null && !result.error ? (
                      <Badge variant="muted">실행됨</Badge>
                    ) : null}
                    <div className="ml-auto flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => runCase(c)}
                        className="h-7"
                        disabled={matrixRunning}
                      >
                        <Play className="size-3.5 mr-1" />
                        실행
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (window.confirm(`"${c.name}" 케이스를 삭제하시겠어요?`)) {
                            deleteMut.mutate(c.id);
                          }
                        }}
                        className="h-7 text-foreground-muted hover:text-destructive"
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </div>
                  {Object.entries(c.variableValues).length > 0 ? (
                    <div className="text-[10px] text-foreground-muted font-mono">
                      {Object.entries(c.variableValues)
                        .map(([k, v]) => `${k}=${v}`)
                        .join('  ·  ')}
                    </div>
                  ) : null}
                  {c.expectedContains ? (
                    <div className="text-xs">
                      <span className="text-foreground-muted">expectedContains: </span>
                      <code className="font-mono text-xs">{c.expectedContains}</code>
                    </div>
                  ) : null}
                  {c.note ? <div className="text-xs text-foreground-muted">{c.note}</div> : null}
                  {result ? (
                    <div className="mt-1 rounded-md border border-border bg-surface-muted/30 p-2 text-xs">
                      <div className="text-foreground-muted mb-1">결과 ({result.durationMs}ms)</div>
                      {result.error ? (
                        <div className="text-destructive">{result.error}</div>
                      ) : (
                        <pre className="whitespace-pre-wrap wrap-break-word font-mono text-xs">
                          {result.output}
                        </pre>
                      )}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
